import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, User, KeyRound } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { nome: "", email: "", email_notifiche: "", notifiche_attive: true, telefono: "", royalty_percent: "", tipo_partner: "intermediario", rivendita_percent: "50", note: "", password: "" };

export default function Partners() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/partners").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p, password: "" }); setEditId(p.id); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Il nome è obbligatorio");
    try {
      const payload = {
        nome: form.nome, email: form.email, telefono: form.telefono,
        email_notifiche: form.email_notifiche || "", notifiche_attive: form.notifiche_attive !== false,
        royalty_percent: parseFloat(form.royalty_percent) || 0, note: form.note,
        tipo_partner: form.tipo_partner || "intermediario",
        rivendita_percent: parseFloat(form.rivendita_percent) || 50,
      };
      if (form.password) payload.password = form.password;
      if (editId) await api.put(`/partners/${editId}`, payload);
      else await api.post("/partners", payload);
      toast.success(editId ? "Partner aggiornato" : "Partner creato");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo partner?")) return;
    await api.delete(`/partners/${id}`); toast.success("Partner eliminato"); load();
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Partner e Royalty</h1>
          <p className="text-sm text-slate-500 mt-1">Collaboratori con percentuale di royalty fissa e accesso in sola lettura.</p>
        </div>
        <Button onClick={openNew} data-testid="add-partner-btn" className="bg-dusty-600 hover:bg-dusty-700 text-white">
          <Plus className="h-4 w-4 mr-1.5" /> Nuovo Partner
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <Card key={p.id} className="p-5 border-slate-200 shadow-sm" data-testid={`partner-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800">{p.nome}</div>
                  {p.email && <div className="text-xs text-slate-500">{p.email}</div>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-partner-${p.id}`}><Pencil className="h-4 w-4 text-slate-500" /></Button>
                <Button size="icon" variant="ghost" onClick={() => del(p.id)} data-testid={`delete-partner-${p.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              {p.tipo_partner === "rivenditore"
                ? <Badge className="bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-50" data-testid={`partner-badge-${p.id}`}>Rivenditore · prezzo riservato + IVA</Badge>
                : <Badge className="bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-50" data-testid={`partner-badge-${p.id}`}>Intermediario · Royalty {p.royalty_percent}%</Badge>}
              {p.has_account
                ? <span className="text-xs text-emerald-600 flex items-center gap-1"><KeyRound className="h-3 w-3" /> Accesso attivo</span>
                : <span className="text-xs text-slate-400">Nessun accesso</span>}
            </div>
            {p.telefono && <div className="text-sm text-slate-500 mt-2">Tel. {p.telefono}</div>}
          </Card>
        ))}
        {items.length === 0 && <div className="text-slate-400 col-span-full py-12 text-center">Nessun partner. Aggiungine uno.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="partner-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Modifica Partner" : "Nuovo Partner"}</DialogTitle>
            <DialogDescription>Imposta una email e password per dare al partner l'accesso in sola lettura.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nome *</Label><Input value={form.nome} onChange={set("nome")} data-testid="partner-nome" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email (accesso)</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="partner-email" className="mt-1.5" /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={set("telefono")} className="mt-1.5" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo partner</Label>
                <Select value={form.tipo_partner} onValueChange={(v) => setForm({ ...form, tipo_partner: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="partner-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="intermediario">Intermediario (royalty %)</SelectItem>
                    <SelectItem value="rivenditore">Rivenditore (fattura da ICS)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.tipo_partner === "rivenditore"
                ? <div className="flex flex-col justify-end"><div className="rounded-lg bg-violet-50 border border-violet-200 p-2.5 text-[11px] text-violet-800">ICS fattura al rivenditore il <b>prezzo riservato</b> impostato su ogni corso (Catalogo Servizi) + IVA. Nessuna percentuale da impostare qui.</div></div>
                : <div><Label>Royalty %</Label><Input type="number" step="0.1" value={form.royalty_percent} onChange={set("royalty_percent")} data-testid="partner-royalty" className="mt-1.5" /></div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Password {editId && "(lascia vuoto per non cambiare)"}</Label><Input type="password" value={form.password} onChange={set("password")} data-testid="partner-password" className="mt-1.5" /></div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-700 font-medium">Notifiche via email</Label>
                <label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-600">
                  <input type="checkbox" checked={form.notifiche_attive !== false} onChange={(e) => setForm({ ...form, notifiche_attive: e.target.checked })} data-testid="partner-notif-attive" className="h-4 w-4 accent-dusty-600" />
                  {form.notifiche_attive !== false ? "Attive" : "Disattivate"}
                </label>
              </div>
              <Input type="email" value={form.email_notifiche} onChange={set("email_notifiche")} data-testid="partner-email-notifiche"
                placeholder="Email alternativa per le notifiche (facoltativa)" disabled={form.notifiche_attive === false} className="bg-white" />
              <p className="text-[11px] text-slate-400">Se vuota si usa l'email di accesso. Disattivando, il partner non riceverà email di notifica.</p>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={set("note")} className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="partner-save" className="bg-dusty-600 hover:bg-dusty-700 text-white">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
